import React, { useEffect, useMemo, useRef, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { CallsByProjectIdAndCreatedAtQuery, ListLLMProjectsQuery, LLMCall, LLMProject } from "../../API";
import { callsByProjectIdAndCreatedAt, listLLMProjects } from "../../graphql/queries";
import {
  buildCompressionRequestPayload,
  CompressionResult,
  DiagnosticTimings,
  parseCompressionResponse,
} from "./promptCompressionApi";
import { MessagePart, selectCompressibleMessageParts } from "./promptCompressionMessageParts";

const COMPRESS_URL = "https://compress.usagetap.com/compress";
const DEFAULT_CALL_LIMIT = 100;
const DEFAULT_DAYS_BACK = 30;
const DEFAULT_DELAY_MS = 1500;
const DEFAULT_COMPRESSION_MODE = "model_auto";
const COMPRESSION_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
const BENCHMARK_LOG_PREFIX = "[Prompt compression benchmark]";

type SavingsRow = {
  id: string;
  callId: string;
  promptId: string | null;
  createdAt: string;
  model: string;
  customer: string;
  messagePartId: number;
  role: "system" | "user";
  originalText: string;
  originalSha256: string | null;
  compressedText: string;
  compressedSha256: string | null;
  originalTokens: number;
  compressedTokens: number | null;
  deterministicTokens: number | null;
  tokensSaved: number | null;
  deterministicTokensSaved: number | null;
  modelIncrementalTokensSaved: number | null;
  savingsPercent: number | null;
  modelStage: string;
  modelRan: boolean | null;
  fallbackUsed: boolean | null;
  attributionResidualTokens: number | null;
  inputCostBefore: number;
  inputCostAfter: number | null;
  inputCostSaved: number | null;
  compressionMode: string;
  compressionPath: string;
  executionType: string;
  elapsedMs: number | null;
  totalResponseMs: number;
  tokenEstimator: string;
  diagnosticTimings: DiagnosticTimings;
  diagnosticTimingsJson: string;
  modelGateDecision: string;
  modelGateReason: string;
  warnings: string;
  status: "compressed" | "error";
  error?: string;
};

type BenchmarkExport = {
  cohortId: string;
  runId: string;
  createdAt: string;
  projectId: string;
  projectName: string;
  sourceWindowStart: string | null;
  sourceWindowEnd: string;
  callLimit: number;
  targetMessages: number;
  mode: string;
  aggressiveness: number;
  requestDelayMs: number;
};

type PromptRecord = {
  prompt_id: string;
  role: "system" | "user";
  text: string;
  conversation_id?: string;
  tenant_id: string;
};

const createAbortError = () => new DOMException("Analysis cancelled", "AbortError");

const sleep = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
  if (signal?.aborted) {
    reject(createAbortError());
    return;
  }

  const handleAbort = () => {
    window.clearTimeout(timeout);
    reject(createAbortError());
  };
  const timeout = window.setTimeout(() => {
    signal?.removeEventListener("abort", handleAbort);
    resolve();
  }, Math.max(0, ms));
  signal?.addEventListener("abort", handleAbort, { once: true });
});

const isAbortError = (error: unknown) => error instanceof Error && error.name === "AbortError";

const formatTimingBreakdown = (timings: DiagnosticTimings) => {
  const total = timings.total_ms;
  const model = timings.llmlingua_ms;
  if (total == null && model == null) return "";
  return [
    total == null ? "" : `total ${total.toFixed(1)} ms`,
    model == null ? "" : `LLMLingua2 ${model.toFixed(1)} ms`,
  ].filter(Boolean).join("; ");
};

const diagnosticTimingLabel = (key: string) => key
  .replace(/_ms$/, "")
  .split("_")
  .map((part) => part === "llmlingua" ? "LLMLingua2" : `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
  .join(" ");

const estimateTokens = (text: string) => Math.ceil(text.trim().length / 4);

const sha256Hex = async (value: string): Promise<string | null> => {
  if (!globalThis.crypto?.subtle) return null;
  try {
    const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
};

const compactTimestamp = (value: string) => value.replace(/[-:.TZ]/g, "");

const slugify = (value: string) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "") || "project";

const safeFilePart = (value: string) => value.replace(/[^a-zA-Z0-9._-]+/g, "-");

const logBenchmark = (event: string, details: Record<string, unknown> = {}) => {
  console.info(BENCHMARK_LOG_PREFIX, event, details);
};

const getInputPricePerMillion = (call: LLMCall): number => {
  const modelPrice = Number(call.model?.inputTokenPrice);
  if (Number.isFinite(modelPrice) && modelPrice > 0) return modelPrice;

  const servicePrice = Number(call.service?.inputTokenPricePerMillion);
  if (Number.isFinite(servicePrice) && servicePrice > 0) return servicePrice;

  return 0;
};

const isS3Reference = (content: string) =>
  content.startsWith("s3://") || content.startsWith("S3:s3://");

const resolveCallContent = async (
  call: LLMCall,
  fieldType: "messages" | "prompt",
  content: string,
  signal: AbortSignal
): Promise<string> => {
  if (!isS3Reference(content)) return content;

  const download = await API.get(
    "eventapi",
    `/getCallLargeContentDownloadUrl/${call.id}/${fieldType}`,
    {
      // Read through the authenticated API instead of fetching the presigned
      // S3 URL in the browser. The bucket intentionally does not allow the
      // production app origin via CORS.
      queryStringParameters: { includeContent: "true" },
    }
  ) as { content?: unknown };

  if (signal.aborted) throw createAbortError();
  if (typeof download.content !== "string") {
    throw new Error(`No content was returned for S3-backed ${fieldType}`);
  }
  return download.content;
};

const extractCompressibleMessageParts = async (call: LLMCall, signal: AbortSignal): Promise<MessagePart[]> => {
  logBenchmark("extracting message parts", { callId: call.id, hasMessages: Boolean(call.messages), hasPrompt: Boolean(call.prompt) });
  if (call.messages) {
    try {
      const messagesContent = await resolveCallContent(call, "messages", call.messages, signal);
      const parsed = JSON.parse(messagesContent);
      const messages = Array.isArray(parsed) ? parsed : parsed?.messages;
      if (Array.isArray(messages)) {
        const messageParts = selectCompressibleMessageParts(messages);
        logBenchmark("extracted message parts", {
          callId: call.id,
          storedMessageCount: messages.length,
          selectedMessageParts: messageParts.map(({ messagePartId, role, content }) => ({
            messagePartId,
            role,
            characterCount: content.length,
          })),
        });
        return messageParts;
      }
    } catch (error) {
      if (isAbortError(error)) throw error;
      console.warn(BENCHMARK_LOG_PREFIX, "unable to parse call messages", { callId: call.id, error });
      if (isS3Reference(call.messages)) {
        throw new Error(`Unable to read S3-backed messages: ${error instanceof Error ? error.message : "Unknown error"}`);
      }
      console.warn("Unable to parse call messages", call.id, error);
    }
  }

  if (!call.prompt) return [];
  const prompt = await resolveCallContent(call, "prompt", call.prompt, signal);
  const messageParts = prompt.trim() ? [{ role: "user" as const, content: prompt, messagePartId: 0 }] : [];
  logBenchmark("extracted prompt fallback", {
    callId: call.id,
    selectedMessageParts: messageParts.map(({ messagePartId, role, content }) => ({
      messagePartId,
      role,
      characterCount: content.length,
    })),
  });
  return messageParts;
};

const compressText = async (
  text: string,
  aggressiveness: number,
  compressionMode: string,
  signal: AbortSignal
): Promise<CompressionResult> => {
  logBenchmark("sending compression request", { characterCount: text.length, aggressiveness, compressionMode });
  const requestController = new AbortController();
  let timedOut = false;
  const abortRequest = () => requestController.abort();
  const timeout = window.setTimeout(() => {
    timedOut = true;
    requestController.abort();
  }, COMPRESSION_REQUEST_TIMEOUT_MS);
  signal.addEventListener("abort", abortRequest, { once: true });

  try {
    const response = await fetch(COMPRESS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: requestController.signal,
      body: JSON.stringify(buildCompressionRequestPayload(text, aggressiveness, compressionMode)),
    });

    if (!response.ok) {
      console.error(BENCHMARK_LOG_PREFIX, "compression request failed", { status: response.status, statusText: response.statusText });
      throw new Error(`Compression failed with HTTP ${response.status}`);
    }

    const parsed = parseCompressionResponse(await response.json(), compressionMode);
    logBenchmark("received compression response", {
      originalTokens: parsed.originalTokens,
      compressedTokens: parsed.compressedTokens,
      compressionPath: parsed.compressionPath,
      executionType: parsed.executionType,
      elapsedMs: parsed.elapsedMs,
    });
    return parsed;
  } catch (error) {
    if (timedOut) {
      console.error(BENCHMARK_LOG_PREFIX, "compression request timed out", { timeoutMs: COMPRESSION_REQUEST_TIMEOUT_MS });
      throw new Error("Compression request timed out after 10 minutes");
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
    signal.removeEventListener("abort", abortRequest);
  }
};

const toCsvValue = (value: unknown) => {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadFile = (filename: string, content: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const downloadCsv = (rows: SavingsRow[]) => {
  const headers = [
    "callId",
    "promptId",
    "createdAt",
    "model",
    "customer",
    "messagePartId",
    "role",
    "originalSha256",
    "compressedSha256",
    "originalTokens",
    "compressedTokens",
    "deterministicTokens",
    "tokensSaved",
    "deterministicTokensSaved",
    "modelIncrementalTokensSaved",
    "savingsPercent",
    "modelStage",
    "modelRan",
    "fallbackUsed",
    "attributionResidualTokens",
    "inputCostBefore",
    "inputCostAfter",
    "inputCostSaved",
    "compressionMode",
    "compressionPath",
    "executionType",
    "elapsedMs",
    "totalResponseMs",
    "tokenEstimator",
    "diagnosticTimingsJson",
    "modelGateDecision",
    "modelGateReason",
    "warnings",
    "status",
    "error",
    "originalText",
    "compressedText",
  ];
  const csv = [
    headers.join(","),
    ...rows.map((row) => headers.map((header) => toCsvValue(row[header as keyof SavingsRow])).join(",")),
  ].join("\n");
  downloadFile(`prompt-compression-savings-${new Date().toISOString()}.csv`, csv, "text/csv;charset=utf-8");
};

const downloadBenchmarkJson = (benchmark: BenchmarkExport, rows: SavingsRow[]) => {
  const conditionId = `mode-${benchmark.mode}__agg-${String(benchmark.aggressiveness).padStart(3, "0")}__compressor-unknown`;
  const filePrefix = `pcbench__cohort-${safeFilePart(benchmark.cohortId)}__condition-${safeFilePart(conditionId)}__run-${safeFilePart(benchmark.runId)}`;
  const manifest = {
    schemaVersion: "pcbench.v1",
    experimentId: "prompt-compression-benchmark",
    cohortId: benchmark.cohortId,
    runId: benchmark.runId,
    createdAt: benchmark.createdAt,
    project: { id: benchmark.projectId, name: benchmark.projectName },
    targetMessages: benchmark.targetMessages,
    cohort: {
      windowStart: benchmark.sourceWindowStart,
      windowEnd: benchmark.sourceWindowEnd,
      callLimit: benchmark.callLimit,
      messageCount: benchmark.targetMessages,
      selectionMethod: "recent-production-calls",
    },
    condition: {
      id: conditionId,
      mode: benchmark.mode,
      aggressiveness: benchmark.aggressiveness / 100,
      aggressivenessDisplay: benchmark.aggressiveness,
      jsonProtection: null,
    },
    compressor: {
      endpoint: COMPRESS_URL,
      commit: null,
      deploymentVersion: null,
      model: null,
      configurationHash: null,
    },
    resolvedSettings: null,
    requestDelayMs: benchmark.requestDelayMs,
    recordCount: rows.length,
  };
  const records = rows.map((row) => ({
    schemaVersion: "pcbench.record.v1",
    experimentId: manifest.experimentId,
    cohortId: benchmark.cohortId,
    runId: benchmark.runId,
    conditionId,
    promptId: row.promptId,
    source: {
      projectId: benchmark.projectId,
      callId: row.callId,
      messagePartId: row.messagePartId,
      role: row.role,
      createdAt: row.createdAt,
      downstreamModel: row.model,
    },
    input: {
      originalText: row.originalText,
      originalSha256: row.originalSha256,
      originalTokens: row.originalTokens,
      characterCount: row.originalText.length,
    },
    output: {
      compressedText: row.compressedText || null,
      compressedSha256: row.compressedSha256,
      compressedTokens: row.compressedTokens,
      tokensSaved: row.tokensSaved,
      savingsPercent: row.savingsPercent,
    },
    stages: {
      deterministicTokens: row.deterministicTokens,
      deterministicTokensSaved: row.deterministicTokensSaved,
      modelIncrementalTokensSaved: row.modelIncrementalTokensSaved,
      modelRan: row.modelRan,
      fallbackUsed: row.fallbackUsed,
      compressionPath: row.compressionPath,
      executionType: row.executionType,
    },
    structure: null,
    integrity: {
      protectedSpanValidationPassed: null,
      jsonRoundTripPassed: null,
      originalCanonicalJsonSha256: null,
      outputCanonicalJsonSha256: null,
      warnings: row.warnings ? row.warnings.split("; ") : [],
    },
    timing: {
      elapsedMs: row.elapsedMs,
      totalResponseMs: row.totalResponseMs,
      diagnosticTimings: row.diagnosticTimings,
    },
    cost: {
      inputCostBefore: row.inputCostBefore,
      inputCostAfter: row.inputCostAfter,
      inputCostSaved: row.inputCostSaved,
    },
    status: row.status,
    error: row.error ?? null,
  }));

  downloadFile(
    `${filePrefix}__benchmark.json`,
    `${JSON.stringify({ manifest, results: records }, null, 2)}\n`,
    "application/json;charset=utf-8"
  );
};

const downloadPromptRecords = (projectName: string, records: PromptRecord[]) => {
  const filename = `prompt-records-${safeFilePart(slugify(projectName))}-${compactTimestamp(new Date().toISOString())}.jsonl`;
  downloadFile(
    filename,
    `${records.map((record) => JSON.stringify(record)).join("\n")}\n`,
    "application/x-ndjson;charset=utf-8"
  );
};

export default function AdminPromptCompressionSavings() {
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState<LLMProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [aggressiveness, setAggressiveness] = useState(50);
  const [callLimit, setCallLimit] = useState(DEFAULT_CALL_LIMIT);
  const [daysBack, setDaysBack] = useState(DEFAULT_DAYS_BACK);
  const [delayMs, setDelayMs] = useState(DEFAULT_DELAY_MS);
  const [compressionMode, setCompressionMode] = useState(DEFAULT_COMPRESSION_MODE);
  const [rows, setRows] = useState<SavingsRow[]>([]);
  const [benchmarkExport, setBenchmarkExport] = useState<BenchmarkExport | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isRunComplete, setIsRunComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const analysisController = useRef<AbortController | null>(null);

  useEffect(() => () => {
    const controller = analysisController.current;
    analysisController.current = null;
    controller?.abort();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      setProjectsLoading(true);
      setProjectsError(null);

      try {
        const result = (await API.graphql(
          graphqlOperation(listLLMProjects, { limit: 1000 })
        )) as { data?: ListLLMProjectsQuery };

        const projectItems = (result.data?.listLLMProjects?.items ?? [])
          .filter(Boolean)
          .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? "")) as LLMProject[];
        setProjects(projectItems);
      } catch (projectError) {
        setProjectsError(projectError instanceof Error ? projectError.message : "Unable to load projects");
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const totals = useMemo(() => {
    const successfulRows = rows.filter((row) => row.status === "compressed");
    const aggregate = successfulRows.reduce(
      (acc, row) => ({
        before: acc.before + row.originalTokens,
        after: acc.after + (row.compressedTokens ?? 0),
        saved: acc.saved + (row.tokensSaved ?? 0),
        deterministicSaved: acc.deterministicSaved + (row.deterministicTokensSaved ?? 0),
        modelSaved: acc.modelSaved + (row.modelIncrementalTokensSaved ?? 0),
        modelRuns: acc.modelRuns + (row.modelRan === true ? 1 : 0),
        fallbacks: acc.fallbacks + (row.fallbackUsed === true ? 1 : 0),
        dollars: acc.dollars + (row.inputCostSaved ?? 0),
        successful: acc.successful + 1,
      }),
      { before: 0, after: 0, saved: 0, deterministicSaved: 0, modelSaved: 0, modelRuns: 0, fallbacks: 0, dollars: 0, successful: 0 }
    );
    const failed = rows.filter((row) => row.status === "error").length;
    return { ...aggregate, failed, savingsPercent: aggregate.before ? (aggregate.saved / aggregate.before) * 100 : 0 };
  }, [rows]);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedRowId) ?? null,
    [rows, selectedRowId]
  );

  const runAnalysis = async () => {
    const analysisStartedAt = new Date();
    const controller = new AbortController();
    analysisController.current?.abort();
    analysisController.current = controller;
    setError(null);
    setRows([]);
    setBenchmarkExport(null);
    setSelectedRowId(null);
    setProgress(0);
    setIsRunning(true);
    setIsRunComplete(false);
    logBenchmark("analysis started", {
      projectId: projectId.trim(),
      daysBack,
      callLimit,
      delayMs,
      aggressiveness,
      compressionMode,
    });

    try {
      const createdAt = daysBack > 0
        ? { ge: new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString() }
        : undefined;

      const result = (await API.graphql(
        graphqlOperation(callsByProjectIdAndCreatedAt, {
          projectId: projectId.trim(),
          createdAt,
          sortDirection: "DESC",
          limit: callLimit,
        })
      )) as { data?: CallsByProjectIdAndCreatedAtQuery };
      if (controller.signal.aborted) throw createAbortError();

      const calls = (result.data?.callsByProjectIdAndCreatedAt?.items ?? []).filter(Boolean) as LLMCall[];
      logBenchmark("calls query completed", { callCount: calls.length, createdAtFilter: createdAt ?? null });
      const workItems: Array<{
        call: LLMCall;
        message: string;
        messagePartId: number;
        role: "system" | "user";
        extractionError?: string;
      }> = [];
      for (const call of calls) {
        if (controller.signal.aborted) throw createAbortError();
        try {
          const messageParts = await extractCompressibleMessageParts(call, controller.signal);
          messageParts.forEach(({ content, messagePartId, role }) => {
            workItems.push({ call, message: content, messagePartId, role });
          });
        } catch (extractionError) {
          if (isAbortError(extractionError)) throw extractionError;
          console.error(BENCHMARK_LOG_PREFIX, "message extraction failed", { callId: call.id, extractionError });
          workItems.push({
            call,
            message: "",
            messagePartId: 0,
            role: "user",
            extractionError: extractionError instanceof Error ? extractionError.message : "Unable to extract call messages",
          });
        }
      }

      if (workItems.length === 0) {
        logBenchmark("analysis found no compressible message parts", { callCount: calls.length });
        setError(`No user or system messages were found in the selected ${daysBack > 0 ? `${daysBack}-day window` : "time window"} for this project.`);
        return;
      }

      logBenchmark("message extraction completed", {
        callCount: calls.length,
        workItemCount: workItems.length,
        roles: workItems.reduce<Record<string, number>>((counts, item) => {
          counts[item.role] = (counts[item.role] ?? 0) + 1;
          return counts;
        }, {}),
      });

      const project = projects.find((item) => item.id === projectId.trim());
      const timestamp = compactTimestamp(analysisStartedAt.toISOString());
      const projectSlug = slugify(project?.name ?? projectId.trim());
      setBenchmarkExport({
        cohortId: `${timestamp.slice(0, 8)}-${projectSlug}-${timestamp.slice(8)}`,
        runId: `${timestamp}-${compressionMode}-agg-${String(aggressiveness).padStart(3, "0")}`,
        createdAt: analysisStartedAt.toISOString(),
        projectId: projectId.trim(),
        projectName: project?.name ?? projectId.trim(),
        sourceWindowStart: createdAt?.ge ?? null,
        sourceWindowEnd: analysisStartedAt.toISOString(),
        callLimit,
        targetMessages: workItems.length,
        mode: compressionMode,
        aggressiveness,
        requestDelayMs: delayMs,
      });

      const nextRows: SavingsRow[] = [];
      for (let index = 0; index < workItems.length; index += 1) {
        if (controller.signal.aborted) throw createAbortError();
        const { call, message, messagePartId, role, extractionError } = workItems[index];
        const estimatedOriginalTokens = estimateTokens(message);
        const pricePerMillion = getInputPricePerMillion(call);
        const estimatedInputCostBefore = (estimatedOriginalTokens / 1_000_000) * pricePerMillion;
        const originalSha256 = await sha256Hex(message);
        const promptHash = await sha256Hex(`${projectId.trim()}|${call.id}|${messagePartId}|${message}`);
        const promptId = promptHash ? `sha256:${promptHash}` : null;
        logBenchmark("processing message part", {
          item: index + 1,
          totalItems: workItems.length,
          callId: call.id,
          messagePartId,
          role,
          characterCount: message.length,
          hasPromptId: Boolean(promptId),
          hasExtractionError: Boolean(extractionError),
        });

        const compressionStartedAt = performance.now();
        try {
          if (extractionError) throw new Error(extractionError);
          const compressionResult = await compressText(message, aggressiveness, compressionMode, controller.signal);
          const compressedText = compressionResult.compressedText;
          const compressedTokens = compressionResult.compressedTokens ?? estimateTokens(compressedText);
          const compressedSha256 = await sha256Hex(compressedText);
          const originalTokens = compressionResult.originalTokens ?? estimatedOriginalTokens;
          const savings = compressionResult.tokenSavings;
          const tokensSaved = savings.totalTokensSaved ?? originalTokens - compressedTokens;
          const deterministicTokens = savings.afterDeterministicTokens;
          const deterministicTokensSaved = savings.deterministicTokensSaved;
          const modelIncrementalTokensSaved = savings.modelIncrementalTokensSaved;
          const warnings = [...compressionResult.warnings];
          if (!compressedText.trim()) {
            warnings.push("Compression returned an empty result (0 output tokens)");
          }
          if (deterministicTokens == null || deterministicTokensSaved == null || modelIncrementalTokensSaved == null) {
            warnings.push("The compression response did not include a complete token savings breakdown");
          }

          const inputCostBefore = (originalTokens / 1_000_000) * pricePerMillion;
          const inputCostAfter = (compressedTokens / 1_000_000) * pricePerMillion;
          const totalResponseMs = Math.round(performance.now() - compressionStartedAt);
          const nextRow: SavingsRow = {
            id: `${call.id}-${messagePartId}`,
            callId: call.id,
            promptId,
            createdAt: call.createdAt,
            model: call.model?.name ?? call.modelId ?? "Unknown",
            customer: call.customer?.name ?? call.customerId ?? "Unknown",
            messagePartId,
            role,
            originalText: message,
            originalSha256,
            compressedText,
            compressedSha256,
            originalTokens,
            compressedTokens,
            deterministicTokens,
            tokensSaved,
            deterministicTokensSaved,
            modelIncrementalTokensSaved,
            savingsPercent: savings.totalReduction != null
              ? savings.totalReduction * 100
              : originalTokens ? (tokensSaved / originalTokens) * 100 : 0,
            modelStage: savings.modelStage,
            modelRan: savings.modelRan,
            fallbackUsed: savings.fallbackUsed,
            attributionResidualTokens: savings.attributionResidualTokens,
            inputCostBefore,
            inputCostAfter,
            inputCostSaved: inputCostBefore - inputCostAfter,
            compressionMode: compressionResult.compressionMode,
            compressionPath: compressionResult.compressionPath,
            executionType: compressionResult.executionType,
            elapsedMs: compressionResult.elapsedMs,
            totalResponseMs,
            tokenEstimator: compressionResult.tokenEstimator,
            diagnosticTimings: compressionResult.diagnosticTimings,
            diagnosticTimingsJson: JSON.stringify(compressionResult.diagnosticTimings),
            modelGateDecision: compressionResult.modelGateDecision,
            modelGateReason: compressionResult.modelGateReason,
            warnings: warnings.join("; "),
            status: "compressed",
          };
          nextRows.push(nextRow);
          logBenchmark("message part compressed", {
            item: index + 1,
            totalItems: workItems.length,
            callId: call.id,
            messagePartId,
            role,
            originalTokens,
            compressedTokens,
            tokensSaved,
          });
        } catch (rowError) {
          if (isAbortError(rowError)) throw rowError;
          console.error(BENCHMARK_LOG_PREFIX, "message part failed", {
            item: index + 1,
            totalItems: workItems.length,
            callId: call.id,
            messagePartId,
            role,
            rowError,
          });
          const totalResponseMs = Math.round(performance.now() - compressionStartedAt);
          nextRows.push({
            id: `${call.id}-${messagePartId}`,
            callId: call.id,
            promptId,
            createdAt: call.createdAt,
            model: call.model?.name ?? call.modelId ?? "Unknown",
            customer: call.customer?.name ?? call.customerId ?? "Unknown",
            messagePartId,
            role,
            originalText: message,
            originalSha256,
            compressedText: "",
            compressedSha256: null,
            originalTokens: estimatedOriginalTokens,
            compressedTokens: null,
            deterministicTokens: null,
            tokensSaved: null,
            deterministicTokensSaved: null,
            modelIncrementalTokensSaved: null,
            savingsPercent: null,
            modelStage: "",
            modelRan: null,
            fallbackUsed: null,
            attributionResidualTokens: null,
            inputCostBefore: estimatedInputCostBefore,
            inputCostAfter: null,
            inputCostSaved: null,
            compressionMode,
            compressionPath: "error",
            executionType: "Error",
            elapsedMs: null,
            totalResponseMs,
            tokenEstimator: "",
            diagnosticTimings: {},
            diagnosticTimingsJson: "{}",
            modelGateDecision: "",
            modelGateReason: "",
            warnings: "",
            status: "error",
            error: rowError instanceof Error ? rowError.message : "Unknown compression error",
          });
        }

        setRows([...nextRows]);
        setSelectedRowId((current) => current ?? nextRows[0]?.id ?? null);
        setProgress(((index + 1) / workItems.length) * 100);
        if (index < workItems.length - 1) await sleep(delayMs, controller.signal);
      }
      setIsRunComplete(true);
    } catch (runError) {
      if (!isAbortError(runError)) {
        console.error(BENCHMARK_LOG_PREFIX, "analysis failed", { runError });
        setError(runError instanceof Error ? runError.message : "Unable to run compression analysis");
      } else {
        logBenchmark("analysis cancelled");
      }
    } finally {
      if (analysisController.current === controller) {
        analysisController.current = null;
        setIsRunning(false);
        logBenchmark("analysis finished");
      }
    }
  };

  const downloadRecords = async () => {
    const downloadStartedAt = new Date();
    const controller = new AbortController();
    const selectedProjectId = projectId.trim();
    analysisController.current?.abort();
    analysisController.current = controller;
    setError(null);
    setProgress(0);
    setIsRunning(true);
    logBenchmark("record export started", { projectId: selectedProjectId, daysBack, callLimit });

    try {
      const createdAt = daysBack > 0
        ? { ge: new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString() }
        : undefined;
      const result = (await API.graphql(
        graphqlOperation(callsByProjectIdAndCreatedAt, {
          projectId: selectedProjectId,
          createdAt,
          sortDirection: "DESC",
          limit: callLimit,
        })
      )) as { data?: CallsByProjectIdAndCreatedAtQuery };
      if (controller.signal.aborted) throw createAbortError();

      const calls = (result.data?.callsByProjectIdAndCreatedAt?.items ?? []).filter(Boolean) as LLMCall[];
      const records: PromptRecord[] = [];
      const extractionErrors: string[] = [];

      for (let callIndex = 0; callIndex < calls.length; callIndex += 1) {
        if (controller.signal.aborted) throw createAbortError();
        const call = calls[callIndex];
        try {
          const messageParts = await extractCompressibleMessageParts(call, controller.signal);
          messageParts.forEach(({ content, messagePartId, role }) => {
            records.push({
              prompt_id: `${call.id}-${messagePartId}`,
              role,
              text: content,
              ...(call.conversationId ? { conversation_id: call.conversationId } : {}),
              tenant_id: selectedProjectId,
            });
          });
        } catch (extractionError) {
          if (isAbortError(extractionError)) throw extractionError;
          console.error(BENCHMARK_LOG_PREFIX, "record export message extraction failed", { callId: call.id, extractionError });
          extractionErrors.push(call.id);
        }
        setProgress(((callIndex + 1) / Math.max(calls.length, 1)) * 100);
      }

      if (!records.length) {
        setError(`No user or system messages were found in the selected ${daysBack > 0 ? `${daysBack}-day window` : "time window"} for this project.`);
        return;
      }

      const project = projects.find((item) => item.id === selectedProjectId);
      downloadPromptRecords(project?.name ?? selectedProjectId, records);
      if (extractionErrors.length) {
        setError(`Downloaded ${records.length} records. ${extractionErrors.length} call${extractionErrors.length === 1 ? "" : "s"} could not be read and were skipped.`);
      }
      logBenchmark("record export completed", {
        startedAt: downloadStartedAt.toISOString(),
        callCount: calls.length,
        recordCount: records.length,
        extractionErrorCount: extractionErrors.length,
      });
    } catch (downloadError) {
      if (!isAbortError(downloadError)) {
        console.error(BENCHMARK_LOG_PREFIX, "record export failed", { downloadError });
        setError(downloadError instanceof Error ? downloadError.message : "Unable to download prompt records");
      } else {
        logBenchmark("record export cancelled");
      }
    } finally {
      if (analysisController.current === controller) {
        analysisController.current = null;
        setIsRunning(false);
        setProgress(0);
      }
    }
  };

  const cancelAnalysis = () => analysisController.current?.abort();

  const columns: GridColDef[] = [
    { field: "createdAt", headerName: "Created", width: 180 },
    { field: "callId", headerName: "Call ID", width: 220 },
    { field: "messagePartId", headerName: "Part ID", width: 100 },
    { field: "role", headerName: "Role", width: 100 },
    { field: "model", headerName: "Model", width: 180 },
    { field: "originalTokens", headerName: "Before", type: "number", width: 110 },
    { field: "deterministicTokens", headerName: "After deterministic", type: "number", width: 150 },
    { field: "compressedTokens", headerName: "After", type: "number", width: 110 },
    { field: "tokensSaved", headerName: "Saved", type: "number", width: 110 },
    { field: "deterministicTokensSaved", headerName: "Deterministic saved", type: "number", width: 155 },
    { field: "modelIncrementalTokensSaved", headerName: "Model incremental saved", type: "number", width: 190 },
    { field: "savingsPercent", headerName: "Saved %", type: "number", width: 110, valueFormatter: (value?: number | null) => value == null ? "" : `${value.toFixed(1)}%` },
    { field: "inputCostSaved", headerName: "Input $ Saved", type: "number", width: 140, valueFormatter: (value?: number | null) => value == null ? "" : `$${value.toFixed(6)}` },
    { field: "executionType", headerName: "Execution", width: 160 },
    { field: "modelStage", headerName: "Model stage", width: 130 },
    { field: "modelRan", headerName: "LLMLingua2 ran", type: "boolean", width: 145 },
    { field: "fallbackUsed", headerName: "Fallback", type: "boolean", width: 110 },
    { field: "attributionResidualTokens", headerName: "Residual", type: "number", width: 110 },
    { field: "compressionPath", headerName: "Path", width: 180 },
    { field: "compressionMode", headerName: "Mode", width: 140 },
    { field: "elapsedMs", headerName: "Elapsed ms", type: "number", width: 120 },
    { field: "totalResponseMs", headerName: "Total response ms", type: "number", width: 150 },
    { field: "diagnosticTimings", headerName: "Timing breakdown", width: 250, valueFormatter: (value?: DiagnosticTimings) => formatTimingBreakdown(value ?? {}) },
    { field: "modelGateDecision", headerName: "ROI gate", width: 120 },
    { field: "modelGateReason", headerName: "ROI gate reason", width: 240 },
    { field: "tokenEstimator", headerName: "Token Estimator", width: 220 },
    { field: "warnings", headerName: "Warnings", width: 220 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "error", headerName: "Error", width: 220 },
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>UsageTap prompt compression savings</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Analyze recent calls for a project, compress each user- and system-role message once, attribute deterministic and model-stage savings from API diagnostics, and export one CSV row per message part.
      </Typography>

      <Card sx={{ mb: 3 }}><CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={isRunning || projectsLoading}>
              <InputLabel id="compression-project-label">Project</InputLabel>
              <Select
                labelId="compression-project-label"
                label="Project"
                value={projectId}
                onChange={(event) => setProjectId(event.target.value)}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name || "Unnamed project"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth disabled={isRunning}>
              <InputLabel id="compression-mode-label">Mode</InputLabel>
              <Select
                labelId="compression-mode-label"
                label="Mode"
                value={compressionMode}
                onChange={(event) => setCompressionMode(event.target.value)}
              >
                <MenuItem value="model_auto">Model auto</MenuItem>
                <MenuItem value="deterministic">Deterministic</MenuItem>
                <MenuItem value="model_force">Model force</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}><Typography gutterBottom>Aggressiveness: {aggressiveness}</Typography><Slider value={aggressiveness} min={0} max={100} onChange={(_, value) => setAggressiveness(value as number)} disabled={isRunning} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth type="number" label="Call limit" value={callLimit} inputProps={{ min: 1, max: 1000 }} onChange={(event) => setCallLimit(Math.min(1000, Math.max(1, Number(event.target.value) || 1)))} disabled={isRunning} helperText="Default 100" /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth type="number" label="Days back" value={daysBack} inputProps={{ min: 0 }} onChange={(event) => setDaysBack(Math.max(0, Number(event.target.value)))} disabled={isRunning} helperText="30 default, 0 = all" /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth type="number" label="Delay ms" value={delayMs} inputProps={{ min: 0 }} onChange={(event) => setDelayMs(Math.max(0, Number(event.target.value) || 0))} disabled={isRunning} /></Grid>
          <Grid item xs={12} md={4}><Stack direction="row" spacing={1}><Button variant="contained" onClick={runAnalysis} disabled={isRunning || !projectId.trim()}>Analyze</Button>{isRunning && <Button color="error" variant="outlined" onClick={cancelAnalysis}>Cancel</Button>}<Button variant="outlined" onClick={downloadRecords} disabled={isRunning || !projectId.trim()}>Download records JSON</Button><Button variant="outlined" onClick={() => downloadCsv(rows)} disabled={!isRunComplete || rows.length === 0}>Download CSV</Button><Button variant="outlined" onClick={() => benchmarkExport && downloadBenchmarkJson(benchmarkExport, rows)} disabled={!isRunComplete || !benchmarkExport || rows.length === 0}>Download benchmark JSON</Button></Stack></Grid>
        </Grid>
        {isRunning && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
        {projectsError && <Alert severity="warning" sx={{ mt: 2 }}>Projects could not be loaded by name: {projectsError}</Alert>}
        {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
      </CardContent></Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Alert severity="info">Before: {totals.before.toLocaleString()} tokens</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity="info">After: {totals.after.toLocaleString()} tokens</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity="success">Saved: {totals.saved.toLocaleString()} tokens</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity="success">Savings rate: {totals.savingsPercent.toFixed(1)}%</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity="success">Deterministic saved: {totals.deterministicSaved.toLocaleString()} tokens</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity="success">Model incremental saved: {totals.modelSaved.toLocaleString()} tokens</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity="success">Input cost saved: ${totals.dollars.toFixed(6)}</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity="info">Compared: {totals.successful.toLocaleString()} messages</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity={totals.failed ? "warning" : "info"}>Failed: {totals.failed.toLocaleString()} messages</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity="info">LLMLingua2 ran: {totals.modelRuns.toLocaleString()} messages</Alert></Grid>
        <Grid item xs={6} md={3}><Alert severity={totals.fallbacks ? "warning" : "info"}>Fallbacks: {totals.fallbacks.toLocaleString()}</Alert></Grid>
      </Grid>

      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedRowId(String(params.id))}
        />
      </Box>
      {selectedRow && Object.keys(selectedRow.diagnosticTimings).length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Diagnostic timing breakdown — {selectedRow.callId} {selectedRow.role} message part {selectedRow.messagePartId}
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(selectedRow.diagnosticTimings).map(([key, value]) => (
                <Grid item xs={6} sm={4} md={3} key={key}>
                  <Typography color="text.secondary" variant="body2">{diagnosticTimingLabel(key)}</Typography>
                  <Typography>{value.toFixed(1)} ms</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
